import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";

import {
  ISitesIssueCounters,
  IIssueMetadata,
  IAlertMetadata,
  ComponentType,
  ISingleDeviceIssue,
  ISingleDeviceAlert,
  IAggregateAlert,
  IMuteAlert,
  ITopActiveIssue,
} from "../../faults/DTOs";
import {
  getActiveAlerts,
  getActiveIssues,
  getFaultsMetadata,
  getSitesActiveIssuesCounters,
  getMutedAlerts,
  muteAlert,
  getUserSubscribedAlerts,
  unmuteAlert,
  getTopActiveIssues,
} from "../../faults/API";
import { RootState } from "../Store";
import {
  ActiveIssuesByComponentCategory,
  IIssueMetadataMap,
  ComponentCategory,
  IAlertMetadataMap,
  IMutedAlert,
} from "../../faults/Models";
import { LoadingState } from "../LoadingState";

interface FaultRefreshState<T> {
  loadingState: LoadingState;
  data?: T;
  lastSuccessfulAt?: number;
  requestId?: string;
  loadingStateBeforeRequest?: LoadingState;
}

interface FaultStoreState {
  activeFaults: {
    [siteId: string]:
      | {
          alerts?: FaultRefreshState<{
            deviceAlerts: ISingleDeviceAlert[];
            aggregatedAlerts: IAggregateAlert[];
          }>;
          issues?: FaultRefreshState<ISingleDeviceIssue[]>;
        }
      | undefined;
  };
  issueCounters: {
    loading: LoadingState;
    data: ISitesIssueCounters;
    requestId?: string;
    loadingBeforeRequest?: LoadingState;
  };
  metadata: {
    loading: LoadingState;
    requestId?: string;
    loadingStateBeforeRequest?: LoadingState;
    issues?: IIssueMetadataMap;
    alerts?: IAlertMetadataMap;
  };
  mutedAlerts: {
    [siteId: string]:
      | {
          loadingState: LoadingState;
          requestId?: string;
          data?: {
            [alertType: number]: IMutedAlert;
          };
        }
      | undefined;
  };
  subscribedAlerts: {
    [siteId: string]:
      | {
          loadingState: LoadingState;
          requestId?: string;
          data?: number[];
        }
      | undefined;
  };
  topActiveIssues: {
    [siteId: string]:
      | {
          loadingState: LoadingState;
          requestId?: string;
          data?: ITopActiveIssue[];
        }
      | undefined;
  };
}

const initialState: FaultStoreState = {
  activeFaults: {},
  issueCounters: {
    loading: LoadingState.None,
    data: {},
  },
  metadata: {
    loading: LoadingState.None,
  },
  mutedAlerts: {},
  subscribedAlerts: {},
  topActiveIssues: {},
};

function beginFaultRefresh<T>(current: FaultRefreshState<T> | undefined, requestId: string) {
  return {
    ...current,
    loadingState: LoadingState.Pending,
    requestId,
    loadingStateBeforeRequest:
      current?.loadingState === LoadingState.Pending
        ? current.loadingStateBeforeRequest
        : (current?.loadingState ?? LoadingState.None),
  };
}

function isCancelledRequest(aborted: boolean, error: { name?: string; code?: string }) {
  return aborted || error.name === "AbortError" || error.code === "ERR_CANCELED";
}

function rejectFaultRefresh<T>(current: FaultRefreshState<T>, cancelled: boolean) {
  current.loadingState = cancelled
    ? (current.loadingStateBeforeRequest ?? LoadingState.None)
    : LoadingState.Error;
  delete current.requestId;
  delete current.loadingStateBeforeRequest;
}

export const multiSitesFaultsSlice = createSlice({
  name: "multiSitesFaultsCounters",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveIssues.pending, (state, action) => {
        const siteId = action.meta.arg;
        state.activeFaults[siteId] ??= {};
        state.activeFaults[siteId]!.issues = beginFaultRefresh(
          state.activeFaults[siteId]!.issues,
          action.meta.requestId
        );
      })
      .addCase(fetchActiveIssues.fulfilled, (state, action) => {
        const siteId = action.meta.arg;
        if (state.activeFaults[siteId]?.issues?.requestId !== action.meta.requestId) {
          return;
        }
        state.activeFaults[siteId]!.issues = {
          loadingState: LoadingState.Complete,
          data: action.payload.activeIssues,
          lastSuccessfulAt: action.payload.receivedAt,
        };
      })
      .addCase(fetchActiveIssues.rejected, (state, action) => {
        const siteId = action.meta.arg;
        const current = state.activeFaults[siteId]?.issues;
        if (current?.requestId !== action.meta.requestId) {
          return;
        }
        rejectFaultRefresh(current, isCancelledRequest(action.meta.aborted, action.error));
      })
      .addCase(fetchActiveAlerts.pending, (state, action) => {
        const siteId = action.meta.arg;
        state.activeFaults[siteId] ??= {};
        state.activeFaults[siteId]!.alerts = beginFaultRefresh(
          state.activeFaults[siteId]!.alerts,
          action.meta.requestId
        );
      })
      .addCase(fetchActiveAlerts.fulfilled, (state, action) => {
        const siteId = action.meta.arg;
        if (state.activeFaults[siteId]?.alerts?.requestId !== action.meta.requestId) {
          return;
        }
        state.activeFaults[siteId]!.alerts = {
          loadingState: LoadingState.Complete,
          data: action.payload.activeAlerts,
          lastSuccessfulAt: action.payload.receivedAt,
        };
      })
      .addCase(fetchActiveAlerts.rejected, (state, action) => {
        const siteId = action.meta.arg;
        const current = state.activeFaults[siteId]?.alerts;
        if (current?.requestId !== action.meta.requestId) {
          return;
        }
        rejectFaultRefresh(current, isCancelledRequest(action.meta.aborted, action.error));
      })
      .addCase(fetchFaultsMetadata.pending, (state, action) => {
        if (state.metadata.loading !== LoadingState.Pending) {
          state.metadata.loadingStateBeforeRequest = state.metadata.loading;
        }
        state.metadata.loading = LoadingState.Pending;
        state.metadata.requestId = action.meta.requestId;
      })
      .addCase(fetchFaultsMetadata.fulfilled, (state, action) => {
        if (state.metadata.requestId !== action.meta.requestId) {
          return;
        }
        const { issues, alerts } = action.payload;
        state.metadata.issues = issues.reduce((accumulator, metadata: IIssueMetadata) => {
          accumulator[metadata.issueType] = metadata;
          return accumulator;
        }, {} as IIssueMetadataMap);
        state.metadata.alerts = alerts.reduce((accumulator, metadata: IAlertMetadata) => {
          accumulator[metadata.alertType] = metadata;
          return accumulator;
        }, {} as IAlertMetadataMap);
        state.metadata.loading = LoadingState.Complete;
        delete state.metadata.requestId;
        delete state.metadata.loadingStateBeforeRequest;
      })
      .addCase(fetchFaultsMetadata.rejected, (state, action) => {
        if (state.metadata.requestId !== action.meta.requestId) {
          return;
        }
        state.metadata.loading = isCancelledRequest(action.meta.aborted, action.error)
          ? (state.metadata.loadingStateBeforeRequest ?? LoadingState.None)
          : LoadingState.Error;
        delete state.metadata.requestId;
        delete state.metadata.loadingStateBeforeRequest;
      })
      .addCase(fetchActiveIssuesCounters.pending, (state, action) => {
        if (state.issueCounters.loading !== LoadingState.Pending) {
          state.issueCounters.loadingBeforeRequest = state.issueCounters.loading;
        }
        state.issueCounters.requestId = action.meta.requestId;
        state.issueCounters.loading = LoadingState.Pending;
      })
      .addCase(fetchActiveIssuesCounters.fulfilled, (state, action) => {
        if (state.issueCounters.requestId !== action.meta.requestId) {
          return;
        }
        delete state.issueCounters.requestId;
        state.issueCounters.data = action.payload;
        state.issueCounters.loading = LoadingState.Complete;
      })
      .addCase(fetchActiveIssuesCounters.rejected, (state, action) => {
        if (state.issueCounters.requestId !== action.meta.requestId) {
          return;
        }
        delete state.issueCounters.requestId;
        state.issueCounters.loading = action.meta.aborted
          ? (state.issueCounters.loadingBeforeRequest ?? LoadingState.None)
          : LoadingState.Error;
      })
      .addCase(fetchMutedAlerts.pending, (state, action) => {
        const siteId = action.meta.arg;
        state.activeFaults[siteId] ??= {};
        state.mutedAlerts[siteId] = beginFaultRefresh(state.mutedAlerts[siteId], action.meta.requestId);
      })
      .addCase(fetchMutedAlerts.fulfilled, (state, action) => {
        const { siteId, mutedAlerts } = action.payload;
        if (state.mutedAlerts[siteId]?.requestId !== action.meta.requestId) {
          return;
        }
        state.mutedAlerts[siteId] = {
          loadingState: LoadingState.Complete,
          data: mutedAlerts.reduce((acc: { [key: number]: IMutedAlert }, alert: IMutedAlert) => {
            acc[alert.alertType] = alert;
            return acc;
          }, {}),
        };
      })
      .addCase(fetchMutedAlerts.rejected, (state, action) => {
        const siteId = action.meta.arg;
        const current = state.mutedAlerts[siteId];
        if (current?.requestId === action.meta.requestId) {
          rejectFaultRefresh(current, action.meta.aborted);
        }
      })
      .addCase(fetchUserSubscribedAlerts.pending, (state, action) => {
        const siteId = action.meta.arg;
        state.subscribedAlerts[siteId] = beginFaultRefresh(
          state.subscribedAlerts[siteId],
          action.meta.requestId
        );
      })
      .addCase(fetchUserSubscribedAlerts.fulfilled, (state, action) => {
        const { siteId, alerts } = action.payload;
        if (state.subscribedAlerts[siteId]?.requestId !== action.meta.requestId) {
          return;
        }
        state.subscribedAlerts[siteId] = {
          loadingState: LoadingState.Complete,
          data: [...new Set(alerts.alerts)],
        };
      })
      .addCase(fetchUserSubscribedAlerts.rejected, (state, action) => {
        const siteId = action.meta.arg;
        const current = state.subscribedAlerts[siteId];
        if (current?.requestId === action.meta.requestId) {
          rejectFaultRefresh(current, action.meta.aborted);
        }
      })
      .addCase(fetchTopActiveIssues.pending, (state, action) => {
        const { siteId } = action.meta.arg;
        state.topActiveIssues[siteId] = beginFaultRefresh(
          state.topActiveIssues[siteId],
          action.meta.requestId
        );
      })
      .addCase(fetchTopActiveIssues.fulfilled, (state, action) => {
        const { siteId, topIssues } = action.payload;
        if (state.topActiveIssues[siteId]?.requestId !== action.meta.requestId) {
          return;
        }
        state.topActiveIssues[siteId] = {
          loadingState: LoadingState.Complete,
          data: topIssues,
        };
      })
      .addCase(fetchTopActiveIssues.rejected, (state, action) => {
        const { siteId } = action.meta.arg;
        const current = state.topActiveIssues[siteId];
        if (current?.requestId === action.meta.requestId) {
          rejectFaultRefresh(current, action.meta.aborted);
        }
      });
  },
});

export const fetchActiveIssues = createAsyncThunk(
  "faults/fetchActiveIssues",
  async (siteId: string, { signal }) => {
    const activeIssues = await getActiveIssues(siteId, signal);
    return {
      siteId,
      activeIssues,
      receivedAt: Date.now(),
    };
  }
);

export const fetchActiveAlerts = createAsyncThunk(
  "faults/fetchActiveAlerts",
  async (siteId: string, { signal }) => {
    const activeAlerts = await getActiveAlerts(siteId, signal);
    return {
      siteId,
      activeAlerts,
      receivedAt: Date.now(),
    };
  }
);

export const fetchFaultsMetadata = createAsyncThunk("faults/fetchMetadata", getFaultsMetadata);

export const fetchActiveIssuesCounters = createAsyncThunk(
  "faults/fetchActiveIssuesCounters",
  async (_, { signal }) => getSitesActiveIssuesCounters(signal)
);

export const fetchMutedAlerts = createAsyncThunk(
  "faults/fetchMutedAlerts",
  async (siteId: string, { signal }) => {
    const mutedAlerts = await getMutedAlerts(siteId, signal);
    return {
      siteId,
      mutedAlerts,
    };
  }
);

export const fetchUserSubscribedAlerts = createAsyncThunk(
  "faults/fetchUserSubscribedAlerts",
  async (siteId: string, { signal }) => {
    const alerts = await getUserSubscribedAlerts(siteId, signal);
    return {
      siteId,
      alerts,
    };
  }
);

export const muteAlertAction = createAsyncThunk(
  "faults/muteAlert",
  async (
    params: { siteId: string; alertType: number; expirationDays: number },
    { dispatch, signal }
  ) => {
    const muteAlertRequest: IMuteAlert = {
      siteId: params.siteId,
      alertType: params.alertType,
      expirationDays: params.expirationDays,
    };
    const mutedAlert = await muteAlert(muteAlertRequest, signal);
    dispatch(fetchMutedAlerts(params.siteId));
    return {
      siteId: params.siteId,
      mutedAlert,
    };
  }
);

export const unmuteAlertAction = createAsyncThunk(
  "faults/unmuteAlert",
  async (params: { siteId: string; muteId: number }, { dispatch, signal }) => {
    await unmuteAlert(params.muteId, params.siteId, signal);
    dispatch(fetchMutedAlerts(params.siteId));
    return {
      muteId: params.muteId,
    };
  }
);

export const selectActiveIssuesByComponentCategory = createSelector(
  [
    (state: RootState) => state.faults.multiSitesFaults.metadata,
    (state: RootState, siteId: string) =>
      state.faults.multiSitesFaults.activeFaults[siteId]?.issues?.data,
  ],
  (metadata, activeIssues) => {
    if (!activeIssues || metadata?.issues === undefined) {
      return undefined;
    }
    return mapActiveIssuesByComponentCategory(activeIssues, metadata.issues);
  }
);

function mapActiveIssuesByComponentCategory(
  deviceIssues: ISingleDeviceIssue[],
  issuesMetadata: IIssueMetadataMap
): ActiveIssuesByComponentCategory {
  return deviceIssues.reduce((accumulator, deviceIssue) => {
    const metadata = issuesMetadata[deviceIssue.issueType];
    if (!metadata) {
      return accumulator;
    }
    const category = componentTypeToCategory(metadata.componentType);
    if (!accumulator[category]) {
      accumulator[category] = [];
    }
    accumulator[category]?.push(deviceIssue);
    return accumulator;
  }, {} as ActiveIssuesByComponentCategory);
}

export function componentTypeToCategory(componentType: ComponentType): ComponentCategory {
  switch (componentType) {
    case ComponentType.Inverter:
      return ComponentCategory.Inverter;
    case ComponentType.Tracker:
      return ComponentCategory.Tracker;
    case ComponentType.System:
      return ComponentCategory.System;
    default:
      return ComponentCategory.Auxiliary;
  }
}

export const selectActiveTrackerIssues = createSelector(
  [(state: RootState, siteId: string) => selectActiveIssuesByComponentCategory(state, siteId)],
  (issuesByComponentCategory) => issuesByComponentCategory?.[ComponentCategory.Tracker]
);

export const fetchTopActiveIssues = createAsyncThunk(
  "faults/fetchTopIssues",
  async (params: { siteId: string; count: number }, { signal }) => {
    const topIssues = await getTopActiveIssues(params.siteId, params.count, signal);
    return {
      siteId: params.siteId,
      topIssues,
    };
  }
);

export const multiSitesFaultsReducer = multiSitesFaultsSlice.reducer;
